import { Reference } from '@approvalmax/types';
import {
    Checkbox,
    CheckboxEditor,
    DateTimeEditor,
    FocusImplementer,
    NumberEditor,
    TextAreaEditor,
} from '@approvalmax/ui';
import { Flex } from '@approvalmax/ui/src/components';
import { intl, mathService, numberHelpers } from '@approvalmax/utils';
import { useAtomValue } from 'jotai';
import { actions, constants } from 'modules/common';
import { domain } from 'modules/data';
import { useDispatch, useSelector } from 'modules/react-redux';
import { ReadOnlyCell } from 'pages/requestForm/components';
import { requestSelector } from 'pages/requestForm/selectors/pageSelectors';
import { getRequestEditMode, RequestEditMode } from 'pages/requestForm/selectors/requestSelectors';
import { useCallback, useMemo } from 'react';
import {
    useNetSuiteExpenseLinesPreviewAttachment,
    useNetSuiteExpenseReportExpenseLinesFieldSettings,
} from 'shared/hooks';
import { netSuiteExpenseLinesState } from 'shared/states';

import FileDropProvider from '../../../../../components/FileDropProvider';
import AttachmentCell from '../../../../../components/lineItemsSection/AttachmentCell/AttachmentCell';
import { NetSuiteExpenseCategory, NetSuiteSalesTaxItem } from '../../../../../data/netsuite/NetSuiteContext';
import { getNetSuiteContext } from '../../../../../selectors/netsuite/netSuiteSelectors';
import { ColumnDefinition } from '../../../../../selectors/types/ColumnDefinition';
import {
    NetSuiteLineItemCategoryCell,
    NetSuiteLineItemCustomerCell,
    NetSuiteLineItemDropdownCell,
    NetSuiteLineItemTaxCodeCell,
} from '../../../components';
import { NetSuiteExpenseLineExtendedExpenseReport } from '../../../data/types';
import { setCurrencyPrecision } from '../../../helpers';
import { useCurrencyPrecision, useGroupEditItems, useLineCustomFields } from '../../../hooks';
import { useExchangeRates } from '../../../hooks/useExchangeRates/useExchangeRates';
import {
    getFileNameWithRequestId,
    isFieldMandatory,
    isFieldReadOnly,
    isFieldVisible,
    isMandatoryFieldEmpty,
} from '../../../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { DropdownEditorContainer } from '../../../NetSuiteRequestForm/NetSuiteRequestForm.styles';
import { companyIdState, netSuiteShowErrorsState } from '../../../resources/states';
import { checkExpenseLineIsEmpty, getLineCurrencyPrecision } from '../../helpers';
import { useExpenseLinesUploadAttachments, useExpenseReportExpenseLines } from '../../hooks';
import {
    netSuiteAttachmentUploadingLineIdsState,
    netSuiteExpenseReportFieldValuesState,
} from '../../NetSuiteExpenseReportRequestForm.states';
import { getGroupEditItemsAvailableFields } from './ExpenseLines.helpers';
import { messages } from './ExpenseLines.messages';
import {
    ColumnDefinitionForExpenseReportExtended,
    onChangeCurrencyHandler,
    onChangeExpenseCategoryHandler,
    onChangeTaxCodeHandler,
} from './ExpenseLines.types';

export const useExpenseLines = (params: {
    workflowId: string;
    currency: Reference | null | undefined;
    requestDate: string | null;
    requestId: string;
    requestAttachments: domain.RequestAttachment[];
}) => {
    const { workflowId, currency, requestDate, requestId, requestAttachments } = params;

    const dispatch = useDispatch();

    const fieldValues = useAtomValue(netSuiteExpenseReportFieldValuesState);
    const expenseLines = useAtomValue(netSuiteExpenseLinesState);
    const companyId = useAtomValue(companyIdState);
    const attachmentUploadingLineIds = useAtomValue(netSuiteAttachmentUploadingLineIdsState);
    const showErrors = useAtomValue(netSuiteShowErrorsState);

    const netSuiteEditingContext = useSelector(getNetSuiteContext);
    const request = useSelector(requestSelector);
    const editMode = useSelector((state) => getRequestEditMode(state, request));
    const isApproverMode = editMode === RequestEditMode.Approver;
    const { currencies, accounts, departments, classes, locations, documentFields, customFields } =
        netSuiteEditingContext;

    const fieldsSettings = useNetSuiteExpenseReportExpenseLinesFieldSettings(documentFields);
    const availableFields = getGroupEditItemsAvailableFields(fieldsSettings);

    const {
        setOneExpenseLine,
        setAllExpenseLines,
        updateCustomField,
        onCloneLine,
        onRemoveLine,
        onFieldUpdate,
        onMultiLineFieldUpdate,
    } = useExpenseReportExpenseLines();

    const { onUploadAttachment } = useExpenseLinesUploadAttachments();

    const { onPreviewAttachment } = useNetSuiteExpenseLinesPreviewAttachment();

    const showErrorsOnEmptyLine = useMemo(() => expenseLines.every(checkExpenseLineIsEmpty), [expenseLines]);

    const { data: exchangeRates } = useExchangeRates(companyId, requestDate, currency?.id, true);

    const currencyPrecision = useCurrencyPrecision(currency);

    const { availableCustomFields, customFieldsColumnDefinition } = useLineCustomFields({
        customFields,
        workflowId,
        sublistType: domain.DocumentSublistType.ExpenseList,
        showErrors,
        showErrorsOnEmptyLine,
        updateCustomField,
    });

    const onUpload = useCallback(
        async (li: NetSuiteExpenseLineExtendedExpenseReport, files: File[]) => {
            const filesWithRequestUuid = files.map((file) => {
                return new File([file], getFileNameWithRequestId(file.name, requestId), {
                    type: file.type,
                    lastModified: file.lastModified,
                });
            });

            const file = filesWithRequestUuid[0];

            const allAttachments = expenseLines.reduce(
                (attachments, expenseLine) => {
                    if (expenseLine.attachment) {
                        attachments.push(expenseLine.attachment);
                    }

                    return attachments;
                },
                [...requestAttachments]
            );

            const isFileUploadedToRequest = allAttachments.some(({ name }) => file.name === name);

            if (isFileUploadedToRequest) {
                dispatch(
                    actions.addErrorToast(
                        messages.uploadErrorDuplicateName({
                            fileName: file.name,
                        })
                    )
                );

                return;
            }

            const attachmentInProgress: domain.RequestAttachment = {
                id: file.name,
                name: file.name,
                size: file.size,
                createdDate: Date.now().toString(),
                attachmentType: domain.RequestAttachmentType.General,
            };

            setOneExpenseLine(li.id, { attachment: attachmentInProgress });

            const attachment = await onUploadAttachment(li.id, filesWithRequestUuid);

            setOneExpenseLine(li.id, { attachment: attachment || undefined });
        },
        [dispatch, onUploadAttachment, requestAttachments, requestId, expenseLines, setOneExpenseLine]
    );

    const onChangeExpenseCategory = useCallback<onChangeExpenseCategoryHandler>(
        (li, value) => {
            if (!value) {
                setOneExpenseLine(li.id, {
                    category: null,
                    expenseAccount: null,
                });

                return;
            }

            const relatedAccount = accounts.find((account) => account.id === value.accountId);

            const categoryRate = value.rates?.find(
                (rate) => rate.currency === fieldValues.currency?.text || rate.currency === fieldValues.currency?.id
            );

            const amount = setCurrencyPrecision(categoryRate?.defaultRate, currencyPrecision) || li.amount;
            const rate = (li.taxRate ?? 0) / 100;

            const taxAmount = numberHelpers.isNumber(amount)
                ? setCurrencyPrecision(mathService.multiply(amount, rate ?? 0), currencyPrecision)
                : 0;

            const grossAmount =
                li.taxCode && numberHelpers.isNumber(amount) && numberHelpers.isNumber(taxAmount)
                    ? setCurrencyPrecision(amount + taxAmount, currencyPrecision)
                    : null;

            setOneExpenseLine(li.id, {
                category: value,
                expenseAccount: relatedAccount || null,
                amount,
                taxAmount,
                grossAmount,
            });
        },
        [accounts, currencyPrecision, fieldValues.currency?.id, fieldValues.currency?.text, setOneExpenseLine]
    );

    const onChangeForeignAmount = useCallback(
        (li: NetSuiteExpenseLineExtendedExpenseReport, value: number | null) => {
            if (!numberHelpers.isNumber(value)) {
                setOneExpenseLine(li.id, {
                    foreignAmount: null,
                    amount: null,
                    taxAmount: null,
                    grossAmount: null,
                });

                return;
            }

            const lineCurrencyPrecision = getLineCurrencyPrecision(li, currencies);

            const foreignAmount = setCurrencyPrecision(value, lineCurrencyPrecision);

            const amount =
                numberHelpers.isNumber(foreignAmount) && numberHelpers.isNumber(li.exchangeRate)
                    ? setCurrencyPrecision(foreignAmount * li.exchangeRate, currencyPrecision)
                    : li.amount;

            const taxAmount =
                numberHelpers.isNumber(amount) && li.taxRate
                    ? setCurrencyPrecision(mathService.multiply(amount, li.taxRate / 100), currencyPrecision)
                    : 0;

            const grossAmount =
                li.taxCode && numberHelpers.isNumber(amount) && numberHelpers.isNumber(taxAmount)
                    ? setCurrencyPrecision(amount + taxAmount, currencyPrecision)
                    : null;

            setOneExpenseLine(li.id, {
                foreignAmount,
                amount,
                taxAmount,
                grossAmount,
            });
        },
        [currencies, currencyPrecision, setOneExpenseLine]
    );

    const onChangeCurrency = useCallback<onChangeCurrencyHandler>(
        (li, value) => {
            if (!value) {
                setOneExpenseLine(li.id, {
                    currency: value,
                    exchangeRate: null,
                    amount: null,
                    grossAmount: null,
                });

                return;
            }

            let exchangeRate: number | null = null;

            if (exchangeRates) {
                const targetExchangeRate = exchangeRates.rates.find((rate) => rate.id === value.id);

                exchangeRate = targetExchangeRate?.rateValue || null;
            }

            const lineCurrencyPrecision = getLineCurrencyPrecision(li, currencies);

            const amount =
                numberHelpers.isNumber(li.foreignAmount) && numberHelpers.isNumber(exchangeRate)
                    ? setCurrencyPrecision(li.foreignAmount * exchangeRate, currencyPrecision)
                    : li.amount;

            const taxAmount = numberHelpers.isNumber(li.taxAmount)
                ? setCurrencyPrecision(li.taxAmount, currencyPrecision)
                : 0;
            const grossAmount =
                li.taxCode && numberHelpers.isNumber(amount)
                    ? setCurrencyPrecision(amount + (taxAmount ?? 0), currencyPrecision)
                    : null;
            const foreignAmount =
                exchangeRate && numberHelpers.isNumber(amount)
                    ? setCurrencyPrecision(amount / exchangeRate, lineCurrencyPrecision)
                    : li.foreignAmount;

            setOneExpenseLine(li.id, {
                currency: value,
                exchangeRate,
                amount,
                taxAmount,
                grossAmount,
                foreignAmount,
            });
        },
        [exchangeRates, currencies, currencyPrecision, setOneExpenseLine]
    );

    const onChangeExchangeRate = useCallback(
        (li: NetSuiteExpenseLineExtendedExpenseReport, value: number | null) => {
            if (!numberHelpers.isNumber(value)) {
                setOneExpenseLine(li.id, {
                    exchangeRate: value,
                    amount: null,
                    grossAmount: null,
                });

                return;
            }

            const lineCurrencyPrecision = getLineCurrencyPrecision(li, currencies);

            const amount = numberHelpers.isNumber(li.foreignAmount)
                ? setCurrencyPrecision(li.foreignAmount * value, currencyPrecision)
                : li.amount;
            const taxAmount = numberHelpers.isNumber(li.taxAmount)
                ? setCurrencyPrecision(li.taxAmount, currencyPrecision)
                : 0;
            const grossAmount =
                li.taxCode && numberHelpers.isNumber(amount)
                    ? setCurrencyPrecision(amount + (taxAmount ?? 0), currencyPrecision)
                    : null;
            const foreignAmount =
                value && numberHelpers.isNumber(amount)
                    ? setCurrencyPrecision(amount / value, lineCurrencyPrecision)
                    : li.foreignAmount;

            setOneExpenseLine(li.id, {
                exchangeRate: value,
                amount,
                taxAmount,
                grossAmount,
                foreignAmount,
            });
        },
        [currencies, currencyPrecision, setOneExpenseLine]
    );

    const onChangeAmount = useCallback(
        (li: NetSuiteExpenseLineExtendedExpenseReport, value: number | null) => {
            const lineCurrencyPrecision = getLineCurrencyPrecision(li, currencies);

            const amount = setCurrencyPrecision(value, currencyPrecision) ?? 0;
            const rate = (li.taxRate ?? 0) / 100;

            const taxAmount = setCurrencyPrecision(mathService.multiply(amount, rate), currencyPrecision);
            const grossAmount = li.taxCode ? setCurrencyPrecision(amount + taxAmount, currencyPrecision) : null;
            const foreignAmount = li.exchangeRate
                ? setCurrencyPrecision(amount / li.exchangeRate, lineCurrencyPrecision)
                : li.foreignAmount;

            setOneExpenseLine(li.id, {
                amount,
                taxAmount,
                grossAmount,
                foreignAmount,
            });
        },
        [currencies, currencyPrecision, setOneExpenseLine]
    );

    const onChangeTaxCode = useCallback<onChangeTaxCodeHandler>(
        (li, value) => {
            const lineCurrencyPrecision = getLineCurrencyPrecision(li, currencies);

            const amount = li.amount ?? 0;
            const rate = (value?.rate ?? 0) / 100;

            const taxAmount = setCurrencyPrecision(mathService.multiply(amount, rate), currencyPrecision);
            const grossAmount = value ? setCurrencyPrecision(amount + taxAmount, currencyPrecision) : null;
            const foreignAmount = li.exchangeRate
                ? setCurrencyPrecision(amount / li.exchangeRate, lineCurrencyPrecision)
                : li.foreignAmount;

            setOneExpenseLine(li.id, {
                taxCode: value,
                taxRate: value?.rate || null,
                taxAmount,
                grossAmount,
                foreignAmount,
            });
        },
        [currencies, currencyPrecision, setOneExpenseLine]
    );

    const onChangeGrossAmount = useCallback(
        (li: NetSuiteExpenseLineExtendedExpenseReport, value: number | null) => {
            if (!numberHelpers.isNumber(value)) {
                setOneExpenseLine(li.id, {
                    amount: null,
                    grossAmount: null,
                    foreignAmount: null,
                });

                return;
            }

            const lineCurrencyPrecision = getLineCurrencyPrecision(li, currencies);

            const grossAmount = setCurrencyPrecision(value, currencyPrecision);

            const rate = (li.taxRate ?? 0) / 100;

            const amount = setCurrencyPrecision(grossAmount / (1 + rate), currencyPrecision);

            const taxAmount = setCurrencyPrecision(grossAmount - amount, currencyPrecision);

            const foreignAmount = li.exchangeRate
                ? setCurrencyPrecision(amount / li.exchangeRate, lineCurrencyPrecision)
                : li.foreignAmount;

            setOneExpenseLine(li.id, {
                amount,
                taxAmount,
                grossAmount,
                foreignAmount,
            });
        },
        [currencies, currencyPrecision, setOneExpenseLine]
    );

    const {
        isShowGroupButtons,
        handleGroupRemove,
        handleGroupClone,
        openGroupEdit,
        closeGroupEdit,
        isGroupEditVisible,
        isGroupRemoveVisible,
        isGroupCloneVisible,
        isGroupEditPopupVisible,
        groupItems,
    } = useGroupEditItems({
        workflowId,
        accounts,
        currencies,
        departments,
        classes,
        locations,
        availableFields,
        availableCustomFields,
        items: expenseLines,
        onCloneLine,
        onRemoveLine,
        onChangeCurrency,
        onChangeExpenseCategory,
        onChangeTaxCode,
        updateCustomField,
        onFieldUpdate,
        onMultiLineFieldUpdate,
    });

    const columnsDefinition = useMemo<ColumnDefinition<NetSuiteExpenseLineExtendedExpenseReport>[]>(() => {
        const definition: ColumnDefinitionForExpenseReportExtended[] = [
            ...(isApproverMode
                ? []
                : [
                      {
                          id: 'checkbox',
                          name: '',
                          fixedWidth: 40,
                          headerCell: (lineItems) => {
                              const checked = lineItems.every((li) => li.checked);
                              const triState = lineItems.some((li) => li.checked);

                              const handleClick = () => {
                                  const nextState = !checked;

                                  setAllExpenseLines({ checked: nextState });
                              };

                              return (
                                  <div onClick={handleClick}>
                                      <Checkbox size='size-18' triState={triState} checked={checked} />
                                  </div>
                              );
                          },
                          cell: (lineItem) => {
                              return (
                                  <div
                                      onClick={() => {
                                          onFieldUpdate(lineItem.id, 'checked', !lineItem.checked);
                                      }}
                                  >
                                      <Checkbox checked={lineItem.checked} size='size-18' />
                                  </div>
                              );
                          },
                      } as ColumnDefinition<NetSuiteExpenseLineExtendedExpenseReport>,
                  ]),
            {
                id: 'refNo',
                name: messages.columnRefNo,
                fixedWidth: 100,
                isHidden: !isFieldVisible(fieldsSettings.refLineNumber),
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (!li.refLineNumber || li.refLineNumber < 1) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li) => (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <>
                                <NumberEditor
                                    ref={registerFocusable}
                                    theme='transparent'
                                    placeholder={messages.columnRefNoPlaceholder}
                                    value={li.refLineNumber || null}
                                    onChange={(value) => {
                                        onFieldUpdate(li.id, 'refLineNumber', value);
                                    }}
                                />
                            </>
                        )}
                    </FocusImplementer>
                ),
            },
            {
                id: 'date',
                name: messages.columnDate,
                fixedWidth: 160,
                isHidden: !isFieldVisible(fieldsSettings.date),
                readonly: () => isFieldReadOnly(fieldsSettings.date),
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (isMandatoryFieldEmpty(li.expenseDate, fieldsSettings.date)) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li) => (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <>
                                <DateTimeEditor
                                    ref={registerFocusable}
                                    theme='transparent'
                                    value={li.expenseDate || ''}
                                    placeholder={messages.columnDatePlaceholder}
                                    onChange={(value) => {
                                        onFieldUpdate(li.id, 'expenseDate', value);
                                    }}
                                />
                            </>
                        )}
                    </FocusImplementer>
                ),
            },
            {
                id: 'category',
                name: messages.columnCategory,
                fixedWidth: 274,
                isHidden: !isFieldVisible(fieldsSettings.category),
                readonly: () => isFieldReadOnly(fieldsSettings.category),
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (isMandatoryFieldEmpty(li.category, fieldsSettings.category)) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.category?.text ?? ''} />
                    ) : (
                        <DropdownEditorContainer>
                            <NetSuiteLineItemCategoryCell
                                invalid={invalid}
                                value={li.category}
                                singleLinePerItem={false}
                                onChange={(value: NetSuiteExpenseCategory | null) => onChangeExpenseCategory(li, value)}
                                placeholder={messages.columnCategoryPlaceholder}
                            />
                        </DropdownEditorContainer>
                    ),
            },
            {
                id: 'expenseAccount',
                name: messages.columnExpenseAccount,
                fixedWidth: 218,
                isHidden: !isFieldVisible(fieldsSettings.account),
                readonly: () => isFieldReadOnly(fieldsSettings.account),
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (isMandatoryFieldEmpty(li.expenseAccount, fieldsSettings.account)) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.expenseAccount?.text ?? ''} />
                    ) : (
                        <DropdownEditorContainer>
                            <NetSuiteLineItemDropdownCell
                                items={accounts}
                                invalid={invalid}
                                value={li.expenseAccount}
                                singleLinePerItem={false}
                                onChange={(value: domain.NetSuiteExpenseLineExpenseReport['expenseAccount']) => {
                                    onFieldUpdate(li.id, 'expenseAccount', value);
                                }}
                                placeholder={messages.columnExpenseAccountPlaceholder}
                            />
                        </DropdownEditorContainer>
                    ),
            },
            {
                id: 'foreignAmount',
                name: messages.columnForeignAmount,
                alignRight: true,
                fixedWidth: 160,
                isHidden: !isFieldVisible(fieldsSettings.foreignAmount),
                readonly: () => isFieldReadOnly(fieldsSettings.foreignAmount),
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    return !numberHelpers.isNumber(li.foreignAmount) && isFieldMandatory(fieldsSettings.foreignAmount);
                },
                cell: (li, { invalid, readonly }) => {
                    const foreignAmountCurrencyPrecision =
                        currencies.find(({ id }) => id === li.currency?.id)?.precision ?? 2;

                    if (readonly) {
                        return (
                            <ReadOnlyCell
                                text={
                                    li.foreignAmount !== null
                                        ? intl.formatNumber(li.foreignAmount, foreignAmountCurrencyPrecision)
                                        : ''
                                }
                            />
                        );
                    }

                    return (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <>
                                    <NumberEditor
                                        theme='transparent'
                                        ref={registerFocusable}
                                        allowFloat
                                        precision={foreignAmountCurrencyPrecision}
                                        max={constants.netSuiteConstants.maxCustomNumericFieldAmount}
                                        min={0}
                                        invalid={invalid}
                                        value={li.foreignAmount}
                                        onChange={(value) => onChangeForeignAmount(li, value)}
                                        placeholder={messages.columnForeignAmountPlaceholder}
                                        align='right'
                                        displayFormat='currency'
                                        changeOnBlur={false}
                                    />
                                </>
                            )}
                        </FocusImplementer>
                    );
                },
            },
            {
                id: 'currency',
                name: messages.columnCurrency,
                alignRight: false,
                fixedWidth: 160,
                isHidden: !isFieldVisible(fieldsSettings.currency),
                readonly: () => isFieldReadOnly(fieldsSettings.currency),
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (isMandatoryFieldEmpty(li.currency, fieldsSettings.currency) || li.isEmpty) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.currency?.text ?? ''} />
                    ) : (
                        <DropdownEditorContainer>
                            <NetSuiteLineItemDropdownCell
                                items={currencies || []}
                                invalid={invalid}
                                value={li.currency}
                                singleLinePerItem={false}
                                onChange={(value: (typeof currencies)[number] | null) => onChangeCurrency(li, value)}
                                placeholder={messages.columnCurrencyPlaceholder}
                            />
                        </DropdownEditorContainer>
                    ),
            },
            {
                id: 'exchangeRate',
                name: messages.columnExchangeRate,
                alignRight: true,
                fixedWidth: 160,
                isHidden: !isFieldVisible(fieldsSettings.exchangeRate),
                readonly: () => isFieldReadOnly(fieldsSettings.exchangeRate),
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (
                        (!li.exchangeRate || li.exchangeRate < 0 || li.isEmpty) &&
                        isFieldMandatory(fieldsSettings.exchangeRate)
                    ) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell
                            text={li.exchangeRate !== null ? intl.formatNumber(li.exchangeRate, 'auto') : ''}
                        />
                    ) : (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <>
                                    <NumberEditor
                                        theme='transparent'
                                        ref={registerFocusable}
                                        allowFloat
                                        precision={8}
                                        max={constants.netSuiteConstants.exchangeRateMaxValue}
                                        min={0}
                                        invalid={invalid}
                                        value={li.exchangeRate}
                                        onChange={(value) => onChangeExchangeRate(li, value)}
                                        placeholder={messages.columnExchangeRatePlaceholder}
                                        align='right'
                                        displayFormat='currency'
                                        changeOnBlur={false}
                                    />
                                </>
                            )}
                        </FocusImplementer>
                    ),
            },
            {
                id: 'amount',
                name: messages.columnAmount,
                alignRight: true,
                fixedWidth: 160,
                isHidden: !isFieldVisible(fieldsSettings.amount),
                readonly: () => isFieldReadOnly(fieldsSettings.amount),
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if ((li.amount == null || li.isEmpty) && isFieldMandatory(fieldsSettings.amount)) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell
                            text={li.amount !== null ? intl.formatNumber(li.amount, currencyPrecision) : ''}
                        />
                    ) : (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <>
                                    <NumberEditor
                                        theme='transparent'
                                        ref={registerFocusable}
                                        allowFloat
                                        precision={currencyPrecision}
                                        max={constants.netSuiteConstants.maxTotalAmount}
                                        min={0}
                                        invalid={invalid}
                                        value={li.amount}
                                        onChange={(value) => onChangeAmount(li, value)}
                                        placeholder={messages.columnAmountPlaceholder}
                                        align='right'
                                        displayFormat='currency'
                                        changeOnBlur={false}
                                    />
                                </>
                            )}
                        </FocusImplementer>
                    ),
            },
            {
                id: 'taxCode',
                name: messages.columnTaxCode,
                fixedWidth: 160,
                isHidden: !isFieldVisible(fieldsSettings.taxCode),
                readonly: () => isFieldReadOnly(fieldsSettings.taxCode),
                invalid: (li) =>
                    showErrors && (showErrorsOnEmptyLine || isMandatoryFieldEmpty(li.taxCode, fieldsSettings.taxCode)),
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.taxCode?.text ?? ''} />
                    ) : (
                        <DropdownEditorContainer>
                            <NetSuiteLineItemTaxCodeCell
                                invalid={invalid}
                                value={li.taxCode}
                                singleLinePerItem={false}
                                onChange={(value: NetSuiteSalesTaxItem) => onChangeTaxCode(li, value)}
                                placeholder={messages.columnTaxCodePlaceholder}
                            />
                        </DropdownEditorContainer>
                    ),
            },
            {
                id: 'taxRate',
                name: messages.columnTaxRate,
                alignRight: true,
                fixedWidth: 160,
                isHidden: !isFieldVisible(fieldsSettings.taxRate),
                readonly: () => isFieldReadOnly(fieldsSettings.taxRate),
                invalid: () => showErrors && showErrorsOnEmptyLine && isFieldMandatory(fieldsSettings.taxRate),
                cell: (li) => <ReadOnlyCell text={numberHelpers.isNumber(li.taxRate) ? `${li.taxRate}%` : ''} />,
            },
            {
                id: 'taxAmount',
                name: messages.columnTaxAmount,
                alignRight: true,
                fixedWidth: 160,
                isHidden: !isFieldVisible(fieldsSettings.taxAmount),
                readonly: () => isFieldReadOnly(fieldsSettings.taxRate),
                invalid: () => showErrors && showErrorsOnEmptyLine && isFieldMandatory(fieldsSettings.taxAmount),
                cell: (li) => (
                    <ReadOnlyCell
                        text={
                            numberHelpers.isNumber(li.amount) && li.taxRate
                                ? intl.formatNumber(li.taxAmount ?? 0, currencyPrecision)
                                : ''
                        }
                    />
                ),
            },
            {
                id: 'grossAmount',
                name: messages.columnGrossAmount,
                alignRight: true,
                fixedWidth: 160,
                isHidden: !isFieldVisible(fieldsSettings.grossAmount),
                readonly: () => isFieldReadOnly(fieldsSettings.grossAmount),
                invalid: (li) => {
                    if (!showErrors && !showErrorsOnEmptyLine) {
                        return false;
                    }

                    return (
                        (li.taxCode !== null || isFieldMandatory(fieldsSettings.grossAmount)) &&
                        !numberHelpers.isNumber(li.grossAmount)
                    );
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell
                            text={li.grossAmount !== null ? intl.formatNumber(li.grossAmount, currencyPrecision) : ''}
                        />
                    ) : (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <>
                                    <NumberEditor
                                        theme='transparent'
                                        ref={registerFocusable}
                                        allowFloat
                                        precision={currencyPrecision}
                                        max={constants.netSuiteConstants.maxTotalAmount}
                                        min={0}
                                        invalid={invalid}
                                        value={li.grossAmount}
                                        onChange={(value) => onChangeGrossAmount(li, value)}
                                        placeholder={messages.columnGrossAmountPlaceholder}
                                        align='right'
                                        displayFormat='currency'
                                        changeOnBlur={false}
                                    />
                                </>
                            )}
                        </FocusImplementer>
                    ),
            },
            {
                id: 'memo',
                name: messages.columnMemo,
                fixedWidth: 274,
                isHidden: !isFieldVisible(fieldsSettings.memo),
                readonly: () => isFieldReadOnly(fieldsSettings.memo),
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (isMandatoryFieldEmpty(li.memo, fieldsSettings.memo)) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.memo || ''} multiline />
                    ) : (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <TextAreaEditor
                                    theme='transparent'
                                    ref={registerFocusable}
                                    invalid={invalid}
                                    value={li.memo || ''}
                                    onChange={(value) => {
                                        onFieldUpdate(li.id, 'memo', value);
                                    }}
                                    placeholder={messages.columnMemoPlaceholder}
                                    maxLength={constants.netSuiteConstants.memoMaxLength}
                                    changeOnBlur
                                />
                            )}
                        </FocusImplementer>
                    ),
            },
            {
                id: 'department',
                name: messages.columnDepartment,
                fixedWidth: 274,
                isHidden: !isFieldVisible(fieldsSettings.department),
                readonly: () => isFieldReadOnly(fieldsSettings.department),
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (isMandatoryFieldEmpty(li.department, fieldsSettings.department)) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.department?.text ?? ''} />
                    ) : (
                        <DropdownEditorContainer>
                            <NetSuiteLineItemDropdownCell
                                items={departments}
                                invalid={invalid}
                                value={li.department}
                                singleLinePerItem={false}
                                onChange={(value: domain.NetSuiteExpenseLineExpenseReport['department']) => {
                                    onFieldUpdate(li.id, 'department', value);
                                }}
                                placeholder={messages.columnDepartmentPlaceholder}
                            />
                        </DropdownEditorContainer>
                    ),
            },
            {
                id: 'class',
                name: messages.columnClass,
                fixedWidth: 180,
                isHidden: !isFieldVisible(fieldsSettings.class),
                readonly: () => isFieldReadOnly(fieldsSettings.class),
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (isMandatoryFieldEmpty(li.class, fieldsSettings.class)) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.class?.text ?? ''} />
                    ) : (
                        <DropdownEditorContainer>
                            <NetSuiteLineItemDropdownCell
                                items={classes}
                                invalid={invalid}
                                value={li.class}
                                singleLinePerItem={false}
                                onChange={(value: domain.NetSuiteExpenseLineExpenseReport['class']) => {
                                    onFieldUpdate(li.id, 'class', value);
                                }}
                                placeholder={messages.columnClassPlaceholder}
                            />
                        </DropdownEditorContainer>
                    ),
            },
            {
                id: 'location',
                name: messages.columnLocation,
                fixedWidth: 180,
                isHidden: !isFieldVisible(fieldsSettings.location),
                readonly: () => isFieldReadOnly(fieldsSettings.location),
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (isMandatoryFieldEmpty(li.location, fieldsSettings.location)) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.location?.text ?? ''} />
                    ) : (
                        <DropdownEditorContainer>
                            <NetSuiteLineItemDropdownCell
                                items={locations}
                                invalid={invalid}
                                value={li.location}
                                singleLinePerItem={false}
                                onChange={(value: domain.NetSuiteExpenseLineExpenseReport['location']) => {
                                    onFieldUpdate(li.id, 'location', value);
                                }}
                                placeholder={messages.columnLocationPlaceholder}
                            />
                        </DropdownEditorContainer>
                    ),
            },
            {
                id: 'customer',
                name: messages.columnCustomer,
                fixedWidth: 218,
                isHidden: !isFieldVisible(fieldsSettings.customer),
                readonly: () => isFieldReadOnly(fieldsSettings.customer),
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (isMandatoryFieldEmpty(li.customer, fieldsSettings.customer)) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.customer?.text ?? ''} />
                    ) : (
                        <DropdownEditorContainer>
                            <NetSuiteLineItemCustomerCell
                                entityId={fieldValues.employee?.id}
                                invalid={invalid}
                                value={li.customer || null}
                                singleLinePerItem={false}
                                onChange={(value: domain.NetSuiteExpenseLineExpenseReport['customer']) => {
                                    onFieldUpdate(li.id, 'customer', value);
                                }}
                                placeholder={messages.columnCustomerPlaceholder}
                            />
                        </DropdownEditorContainer>
                    ),
            },
            {
                id: 'billable',
                name: messages.columnBillable,
                fixedWidth: 80,
                isHidden: !isFieldVisible(fieldsSettings.billable),
                readonly: (li) => !li.customer || isFieldReadOnly(fieldsSettings.billable),
                invalid: () => showErrors && showErrorsOnEmptyLine,
                cell: (li, { readonly }) => (
                    <Flex alignItems='center'>
                        <CheckboxEditor
                            disabled={readonly}
                            value={li.customer ? Boolean(li.isBillable) : false}
                            onChange={(value) => {
                                onFieldUpdate(li.id, 'isBillable', value);
                            }}
                        />
                    </Flex>
                ),
            },
            {
                id: 'attachment',
                name: messages.columnLineAttachment,
                fixedWidth: 250,
                isHidden: !isFieldVisible(fieldsSettings.attachment),
                readonly: () => isFieldReadOnly(fieldsSettings.attachment),
                invalid: (li) =>
                    showErrors &&
                    (showErrorsOnEmptyLine || isMandatoryFieldEmpty(li.attachment, fieldsSettings.attachment)),
                cell: (li, _options) => (
                    <FileDropProvider
                        maxSize={constants.uploadsConstants.COMMENT_MAX_ATTACHMENT_FILESIZE}
                        onUpload={(files) => onUpload(li, files)}
                    >
                        <AttachmentCell
                            isLoading={attachmentUploadingLineIds.includes(li.id)}
                            attachment={li.attachment}
                            onRemoveAttachment={() => setOneExpenseLine(li.id, { attachment: undefined })}
                            onClickOnAttachment={onPreviewAttachment}
                        />
                    </FileDropProvider>
                ),
            },
            {
                id: 'isReceipt',
                name: messages.columnReceipt,
                fixedWidth: 80,
                isHidden: !isFieldVisible(fieldsSettings.receipt),
                readonly: () => isFieldReadOnly(fieldsSettings.receipt),
                invalid: () => showErrors && showErrorsOnEmptyLine,
                cell: (li, { readonly }) => (
                    <Flex alignItems='center'>
                        <CheckboxEditor
                            disabled={readonly}
                            value={Boolean(li.isReceipt)}
                            onChange={(value) => {
                                onFieldUpdate(li.id, 'isReceipt', value);
                            }}
                        />
                    </Flex>
                ),
            },
            {
                id: 'isNonReimbursable',
                name: messages.columnNonReimbursable,
                fixedWidth: 150,
                isHidden: !isFieldVisible(fieldsSettings.nonReimbursable),
                readonly: (li) => li.isCorporateCreditCard || isFieldReadOnly(fieldsSettings.nonReimbursable),
                invalid: () => showErrors && showErrorsOnEmptyLine,
                cell: (li, { readonly }) => (
                    <Flex alignItems='center'>
                        <CheckboxEditor
                            disabled={readonly}
                            value={li.isNonReimbursable}
                            onChange={(value) => {
                                onFieldUpdate(li.id, 'isNonReimbursable', value);
                            }}
                        />
                    </Flex>
                ),
            },
            {
                id: 'isCorporateCreditCard',
                name: messages.columnCorporateCard,
                fixedWidth: 150,
                isHidden: !isFieldVisible(fieldsSettings.corporateCreditCard),
                readonly: (li) =>
                    li.isNonReimbursable || !li.expenseAccount || isFieldReadOnly(fieldsSettings.corporateCreditCard),
                invalid: () =>
                    showErrors && showErrorsOnEmptyLine && isFieldMandatory(fieldsSettings.corporateCreditCard),
                cell: (li, { readonly }) => (
                    <Flex alignItems='center'>
                        <CheckboxEditor
                            disabled={readonly}
                            value={li.isCorporateCreditCard}
                            onChange={(value) => {
                                onFieldUpdate(li.id, 'isCorporateCreditCard', value);
                            }}
                        />
                    </Flex>
                ),
            },

            ...customFieldsColumnDefinition,
        ];

        return definition.filter((col) => !col.isHidden);
    }, [
        isApproverMode,
        fieldsSettings,
        showErrors,
        showErrorsOnEmptyLine,
        accounts,
        currencies,
        departments,
        classes,
        locations,
        currencyPrecision,
        fieldValues.employee?.id,
        attachmentUploadingLineIds,
        customFieldsColumnDefinition,
        onFieldUpdate,
        setOneExpenseLine,
        setAllExpenseLines,
        onUpload,
        onChangeExpenseCategory,
        onChangeForeignAmount,
        onChangeCurrency,
        onChangeExchangeRate,
        onChangeAmount,
        onChangeTaxCode,
        onChangeGrossAmount,
        onPreviewAttachment,
    ]);

    return {
        columnsDefinition,
        isShowGroupButtons,
        handleGroupRemove,
        handleGroupClone,
        openGroupEdit,
        closeGroupEdit,
        isGroupEditVisible,
        isGroupRemoveVisible,
        isGroupCloneVisible,
        isGroupEditPopupVisible,
        groupItems,
    };
};
